import { MenuOutlined, SearchOutlined } from '@ant-design/icons'
import { useAuth } from '@services/auth'
import { useProfile } from '@services/auth/useProfile'
import { IconLogout } from '@shared/icons'
import {
  useMobilePageHeader,
  useMobilePageHeaderSetter,
} from '@shared/innmaxLib/hooks/useMobilePageHeader'
import { useRoutes } from '@shared/innmaxLib/routes/useRoutes'
import { Drawer } from '@shared/innmaxUI'
import cx from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import * as routes from 'src/pages'
import styled from 'styled-components'

type Props = {
  title?: string | React.ReactNode
  actions?: React.ReactNode[]
  onMenuToggle?: (visible: boolean) => void
  avaliablePaths?: string[]
}

function MobileNavbar({
  title,
  actions = [],
  onMenuToggle,
  avaliablePaths = ['/report/operations-report', '/map-dashboard'],
}: Props) {
  const { t } = useTranslation()
  const [menuVisible, setMenuVisible] = React.useState(false)

  const {
    actionNodes,
    enableFilter,
    showShadow,
    title: headerTitle,
  } = useMobilePageHeader()
  const { setToogleKey } = useMobilePageHeaderSetter()

  const handleMenuToggle = () => {
    const newState = !menuVisible
    setMenuVisible(newState)
    onMenuToggle?.(newState)
  }

  return (
    <NavbarContainer showShadow={showShadow}>
      <TitleSection>{headerTitle || title || t('common:menu')}</TitleSection>
      <ActionsSection>
        {actions && actions.length > 0 && <ActionItems>{actions}</ActionItems>}
        {enableFilter && (
          <SearchOutlined
            className="text-primary text-xl"
            onClick={() => setToogleKey('filter')}
          />
        )}
        {actionNodes && actionNodes.length > 0 && (
          <ActionItems>
            {actionNodes.map(node => (
              <div key={node.key} onClick={() => setToogleKey(node.key)}>
                {node.element}
              </div>
            ))}
          </ActionItems>
        )}
        <MenuButton onClick={handleMenuToggle} title={t('common:menu')}>
          <MenuOutlined className="text-primary" />
        </MenuButton>
      </ActionsSection>
      <RouteDrawer
        open={menuVisible}
        avaliablePaths={avaliablePaths}
        onClose={() => setMenuVisible(false)}
      />
    </NavbarContainer>
  )
}

const RouteDrawer = (props: {
  open: boolean
  avaliablePaths: string[]
  onClose?: () => void
}) => {
  const { _routes, renderRouteIcon } = useRoutes(routes)
  const { open, avaliablePaths, onClose } = props

  const { logout } = useAuth()

  const { profile } = useProfile()

  const currentPath = useLocation().pathname

  const filterRoutesByPath = (routes: any[], paths: string[]): any[] => {
    return routes.reduce((acc, route) => {
      const matchesPath = paths.includes(route.path)
      const hasChildren = route.children && route.children.length > 0

      if (matchesPath) {
        acc.push(route)
      } else if (hasChildren) {
        const filteredChildren = filterRoutesByPath(route.children, paths)
        if (filteredChildren.length > 0) {
          acc.push(...filteredChildren)
        }
      }

      return acc
    }, [])
  }

  const filteredRoutes = filterRoutesByPath(_routes, avaliablePaths)

  return (
    <Drawer
      open={open}
      title=""
      closable={false}
      width={'70%'}
      drawerStyle={{
        background: '#272727',
      }}
      headerStyle={{
        border: '0',
      }}
      placement="left"
      onClose={onClose}>
      <div className="m-16">
        <Brand to="/" data-title={profile.name}>
          <img src={profile.url} alt={profile.name} />
          <div>{profile.name}</div>
        </Brand>
      </div>
      <div className="h-[60dvh] overflow-auto">
        {filteredRoutes.map((route: any) => (
          <MenuItem
            key={route.path}
            className={cx({
              selected: currentPath === route.path,
            })}>
            {renderRouteIcon(route)}
            <Link
              to={route.path}
              onClick={onClose}
              className={cx({
                'text-white': currentPath !== route.path,
                'text-primary': currentPath === route.path,
              })}
              style={{ textDecoration: 'none' }}>
              {route.title}
            </Link>
          </MenuItem>
        ))}
      </div>
      <div
        className="flex text-white items-center gap-5 p-16"
        onClick={() => logout()}>
        <IconLogout />
        登出
      </div>
    </Drawer>
  )
}

const MenuItem = styled.div`
  padding: 16px;
  gap: 8px;
  display: flex;
  align-items: center;
  color: #ffffff70;
  &.selected {
    color: ${p => p.theme.primary};
    i > svg > g,
    i > svg > g > g {
      stroke: ${p => p.theme.primary};
    }

    i > svg > path {
      fill: ${p => p.theme.primary};
    }

    i.icon-monthly-billing > svg > path {
      fill: transparent !important;
      stroke: ${p => p.theme.primary};
    }

    i.ant-menu-submenu-arrow {
      color: ${p => p.theme.primary} !important;
    }
  }
`

const Brand = styled(Link)`
  width: 100%;
  margin-bottom: 15px;

  > img {
    width: 100%;
    height: auto;
    max-width: 127px;
    max-height: 60px;
  }

  > div {
    min-height: 8px;
    font-size: 13px;
    font-weight: 500;
    margin-top: 10px;
    color: ${p => p.theme.logoText} !important;
  }
`

const NavbarContainer = styled.div<{ showShadow?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: #272727;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 100;
  box-shadow: ${p =>
    p.showShadow ? '0px 4px 20px 0px rgba(0, 0, 0, 0.5)' : 'none'};
`

const TitleSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  color: ${p => p.theme.primaryText || '#000000'};
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`

const ActionItems = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > * {
    display: flex;
    align-items: center;
  }
`

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  font-size: 20px;
  color: ${p => p.theme.primaryText || '#000000'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }
`

export default MobileNavbar
